import { formatName } from '@/utils/nameFormatter';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin, Input } from 'antd';
import {
    TeamOutlined,
    UserOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    SearchOutlined,
    FireOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import { useReports } from '../context/ReportsContext';
import { getTeamWorkloadReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

// Brand palette
const C = {
    blue: '#00AAF1',
    navy: '#003263',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#a855f7',
    indigo: '#6366f1',
    slate: '#64748b',
};

const card = 'bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs transition duration-200';
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700, marginBottom: 4 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

export default function TeamWorkloadTab() {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(() => getCachedTab('team_workload'));
    const [search, setSearch] = useState('');

    const loadData = useCallback(async () => {
        const cached = getCachedTab('team_workload');
        if (cached) {
            setReportData(cached);
            return;
        }

        setLoading(true);
        try {
            const res = await getTeamWorkloadReport({
                department: filters.department,
                teamMember: filters.teamMember,
                projectId: filters.projectId,
            });
            if (res && res.success && res.result) {
                setReportData(res.result);
                setCachedTab('team_workload', res.result);
            }
        } catch (err) {
            console.error('Error fetching team workload report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters.department, filters.teamMember, filters.projectId, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const members = reportData?.members || [];

    const filteredMembers = useMemo(() => {
        return members.filter(m =>
            !search ||
            (m.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (m.department || '').toLowerCase().includes(search.toLowerCase()) ||
            (m.role || '').toLowerCase().includes(search.toLowerCase())
        );
    }, [members, search]);

    const optimalCount = members.filter(m => (m.utilization || 0) >= 80 && (m.utilization || 0) <= 105).length;
    const availableCount = members.filter(m => (m.utilization || 0) < 80).length;
    const overallocatedCount = members.filter(m => (m.utilization || 0) > 105).length;
    const avgUtilization = members.length > 0
        ? Math.round(members.reduce((acc, m) => acc + (m.utilization || 0), 0) / members.length)
        : 0;

    const distributionDonutData = [
        { name: 'Optimal (80–100%)', value: optimalCount, fill: C.blue },
        { name: 'Available (<80%)', value: availableCount, fill: C.green },
        { name: 'Overallocated (>105%)', value: overallocatedCount, fill: C.red },
    ].filter(d => d.value > 0);

    // Prepare chart data for top 12 loaded members
    const memberChartData = filteredMembers.slice(0, 14).map(m => {
        const formattedName = formatName(m.name || 'Unknown');
        const shortName = formattedName.split(' ')[0] + (formattedName.split(' ')[1] ? ` ${formattedName.split(' ')[1][0]}.` : '');
        return {
            name: shortName,
            fullName: formattedName,
            role: m.role || 'Member',
            department: m.department || 'Operations',
            assignedHours: m.assignedHours || 0,
            capacityHours: 40,
            utilization: m.utilization || 0,
            activeTasks: m.activeTasksCount || 0,
            overdueTasks: m.overdueTasks || 0,
            fill: m.utilization > 105 ? C.red : m.utilization >= 80 ? C.blue : C.green,
        };
    });

    // Department grouping stats
    const deptStats = useMemo(() => {
        const map = {};
        members.forEach(m => {
            const d = m.department || 'General';
            if (!map[d]) map[d] = { department: d, count: 0, totalHours: 0, totalUtil: 0 };
            map[d].count++;
            map[d].totalHours += m.assignedHours || 0;
            map[d].totalUtil += m.utilization || 0;
        });
        return Object.values(map).map(d => ({
            department: d.department,
            memberCount: d.count,
            totalHours: d.totalHours,
            avgUtilization: Math.round(d.totalUtil / (d.count || 1)),
        }));
    }, [members]);

    // Custom Tooltip for Workload Bars
    const MemberBarTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        return (
            <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-3.5 shadow-2xl text-xs space-y-2 max-w-xs">
                <div className="font-bold text-white text-sm border-b border-neutral-800 pb-1">
                    {d.fullName}
                </div>
                <div className="text-slate-400">
                    {d.role} • <span className="text-blue-400">{d.department}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-slate-300 pt-1 border-t border-neutral-800">
                    <div>Committed: <span className="text-white font-bold">{d.assignedHours}h</span></div>
                    <div>Capacity: <span className="text-slate-400">40h / wk</span></div>
                    <div>Utilization: <span className={`font-black ${d.utilization > 105 ? 'text-red-400' : d.utilization >= 80 ? 'text-blue-400' : 'text-emerald-400'}`}>{d.utilization}%</span></div>
                    <div>Active Tasks: <span className="text-white font-bold">{d.activeTasks}</span></div>
                </div>
                {d.overdueTasks > 0 && (
                    <div className="text-red-400 font-semibold pt-1 border-t border-neutral-800">
                        ⚠ {d.overdueTasks} overdue task(s)
                    </div>
                )}
            </div>
        );
    };



    return (
        <Spin spinning={loading} size="large" tip="Loading team workload...">
            <div className="space-y-6 min-h-[400px]">
            {/* Header info banner */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-xs">
                        <TeamOutlined className="text-lg" />
                    </div>
                    <div>
                        <h3 className="text-sm sm:text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                            Team Workload &amp; Capacity Distribution
                        </h3>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            Standard 40-hour capacity baseline. Overallocated members flagged to prevent burnout and delays.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 px-3 py-1.5 rounded-xl bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {members.length} Team Members
                    </span>
                    <span className="text-xs font-bold text-blue-600 dark:text-[#00AAF1] px-3 py-1.5 rounded-xl bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {avgUtilization}% Avg Utilization
                    </span>
                    <TabHelpButton tabKey="team_workload" />
                </div>
            </div>

            {/* Top Scorecard Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between hover:border-blue-500/50 transition">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Optimal (80–100%)</div>
                        <div className="text-3xl font-black text-blue-600 dark:text-[#00AAF1] mt-1 font-headline">
                            {optimalCount} Members
                        </div>
                        <div className="text-xs text-blue-600 dark:text-[#00AAF1] font-semibold mt-1">
                            Balanced focus &amp; high productivity
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-neutral-900 text-blue-500 flex items-center justify-center text-xl">
                        <CheckCircleOutlined />
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between hover:border-emerald-500/50 transition">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Available (&lt;80%)</div>
                        <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 font-headline">
                            {availableCount} Members
                        </div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-1">
                            Open bandwidth for incoming work
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-neutral-900 text-emerald-500 flex items-center justify-center text-xl">
                        <ClockCircleOutlined />
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between hover:border-red-500/50 transition">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Overallocated (&gt;105%)</div>
                        <div className="text-3xl font-black text-red-500 mt-1 font-headline">
                            {overallocatedCount} Members
                        </div>
                        <div className="text-xs text-red-500 font-semibold mt-1">
                            Burnout &amp; bottleneck alert
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-neutral-900 text-red-500 flex items-center justify-center text-xl">
                        <WarningOutlined />
                    </div>
                </div>
            </div>

            {/* Visual Charts: Donut + 40h Composed Bar Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Donut Chart: Workload Risk Distribution */}
                <div className={card}>
                    <div className="mb-4">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Workload Risk Distribution
                        </h3>
                        <p className="text-xs text-slate-400">
                            Breakdown of team capacity thresholds
                        </p>
                    </div>

                    <div className="relative h-60">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={distributionDonutData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="60%"
                                    outerRadius="88%"
                                    paddingAngle={3}
                                    stroke="none"
                                >
                                    {distributionDonutData.map((d) => (
                                        <Cell key={d.name} fill={d.fill} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-2xl font-black text-slate-900 dark:text-white font-headline">
                                {avgUtilization}%
                            </span>
                            <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Avg Load</span>
                        </div>
                    </div>

                    <div className="space-y-2 text-xs font-semibold mt-3">
                        {distributionDonutData.map((d) => (
                            <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                <span className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                                    <span>{d.name}</span>
                                </span>
                                <span className="font-extrabold text-slate-900 dark:text-white">
                                    {d.value} ({members.length > 0 ? Math.round((d.value / members.length) * 100) : 0}%)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Composed Bar Chart: Capacity vs 40h Baseline */}
                <div className={`${card} lg:col-span-2`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                                40-Hour Capacity Baseline vs Assigned Hours
                            </h3>
                            <p className="text-xs text-slate-400">
                                Horizontal reference line indicates the 40h standard weekly baseline
                            </p>
                        </div>
                        <span className="text-xs font-semibold text-slate-400 bg-slate-100 dark:bg-neutral-900 px-2.5 py-1 rounded-lg">
                            Hover for member details
                        </span>
                    </div>

                    <div className="h-72">
                        {memberChartData.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-xs text-slate-400">
                                No members found for this filter.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={memberChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="h" domain={[0, 'dataMax + 10']} />
                                    <Tooltip content={<MemberBarTooltip />} />
                                    <ReferenceLine
                                        y={40}
                                        stroke={C.indigo}
                                        strokeDasharray="4 4"
                                        strokeWidth={2}
                                        label={{ value: '40h Standard Capacity', fill: '#818cf8', fontSize: 11, position: 'top' }}
                                    />
                                    <Bar
                                        dataKey="assignedHours"
                                        name="Assigned Hours"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={42}
                                    >
                                        {memberChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Row 2: Departmental Workload Density Bar Chart */}
            {deptStats.length > 0 && (
                <div className={card}>
                    <div className="mb-4">
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Departmental Capacity &amp; Average Utilization
                        </h3>
                        <p className="text-xs text-slate-400">
                            Comparison of committed hours and average utilization percentage across departments
                        </p>
                    </div>

                    <div className="h-60">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={deptStats} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                                <XAxis dataKey="department" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="%" />
                                <Tooltip {...tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar
                                    dataKey="avgUtilization"
                                    name="Avg Utilization %"
                                    fill={C.blue}
                                    radius={[6, 6, 0, 0]}
                                    maxBarSize={48}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Individual Team Member Cards with Search Filter */}
            <div className={card}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Team Member Workload Scorecards
                        </h3>
                        <p className="text-xs text-slate-400">
                            Detailed individual effort commitments, utilization levels, and active assignments
                        </p>
                    </div>
                    <Input
                        prefix={<SearchOutlined className="text-slate-400" />}
                        placeholder="Search team member or department..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="bg-slate-50 dark:bg-neutral-900 border-slate-200 dark:border-neutral-800 rounded-xl text-xs"
                        style={{ width: 240, height: 38 }}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredMembers.length > 0 ? (
                        filteredMembers.map((m) => {
                            const util = m.utilization || Math.round(((m.assignedHours || 35) / 40) * 100);
                            const isOver = util > 105;
                            const isOptimal = util >= 80 && util <= 105;
                            const badgeColor = isOver
                                ? 'bg-red-500/10 text-red-500 border-red-500/30'
                                : isOptimal
                                ? 'bg-[#00AAF1]/10 text-[#00AAF1] border-[#00AAF1]/30'
                                : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';

                            return (
                                <div
                                    key={m.id}
                                    className="p-4 rounded-xl bg-slate-50/70 dark:bg-neutral-950 border border-slate-200/60 dark:border-neutral-800 hover:border-blue-500/60 transition-all duration-200 hover:shadow-lg space-y-3"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-xs shadow-xs">
                                                {m.name ? m.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'TM'}
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">
                                                    {m.name}
                                                </h4>
                                                <p className="text-[11px] text-slate-400">
                                                    {m.role || 'Member'} • {m.department || 'Operations'}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`text-[11px] font-black px-2 py-0.5 rounded-lg border ${badgeColor}`}>
                                            {isOver && '⚠ '}{util}%
                                        </span>
                                    </div>

                                    {/* Metrics summary */}
                                    <div className="grid grid-cols-3 gap-2 p-2.5 rounded-xl bg-white dark:bg-black border border-slate-200/70 dark:border-neutral-800 text-center">
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase">Assigned</div>
                                            <div className="text-sm font-black text-slate-900 dark:text-white">{m.assignedHours || 0}h</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase">Capacity</div>
                                            <div className="text-sm font-black text-slate-400">40h</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase">Tasks</div>
                                            <div className="text-sm font-black text-blue-500">{m.activeTasksCount || 0}</div>
                                        </div>
                                    </div>

                                    {m.overdueTasks > 0 && (
                                        <div className="text-[11px] text-red-500 font-semibold flex items-center gap-1.5 pt-0.5">
                                            <WarningOutlined />
                                            <span>{m.overdueTasks} overdue sprint task(s)</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full py-8 text-center text-xs text-slate-400">
                            No team members found for this filter.
                        </div>
                    )}
                </div>
            </div>
            </div>
        </Spin>
    );
}
